import { useEffect, useMemo } from 'react';
import styled from 'styled-components';

import { Box } from 'components/Box/Box';
import { CmsApiPagePost } from 'modules/cms/api/types/CmsApiPage';
import { CmsBlockContainerWithDividers } from 'modules/cms/components/BlocksWithContainer/CmsBlockContainerWithDividers';
import {
  CmsBodyHolder,
  CmsBodyHolderWidth,
} from 'modules/cms/components/BodyHolder/CmsBodyHolder';
import { CmsHeaderImage } from 'modules/cms/components/HeaderImage/CmsHeaderImage';
import { CmsListingCountsLoadable } from 'modules/cms/components/ListingCounts/CmsListingCountsLoadable';
import { CmsSocialSidebar } from 'modules/cms/components/SocialSidebar/CmsSocialSidebar';
import { CmsTableOfContentsLoadable } from 'modules/cms/components/TableOfContents/CmsTableOfContentsLoadable';
import { getContentFirstRealBlockIndex } from 'modules/cms/helpers/getContentFirstRealBlockIndex';
import { getContentHasBodyHeader } from 'modules/cms/helpers/getContentHasBodyHeader';
import { getContentListingCounts } from 'modules/cms/helpers/getContentListingCounts';
import { getContentLocaleUrl } from 'modules/cms/helpers/getContentLocaleUrl';
import { getContentShowListingCounts } from 'modules/cms/helpers/getContentShowListingCounts';
import { getNextRealBlock } from 'modules/cms/helpers/getNextRealBlock';
import { getPrevRealBlock } from 'modules/cms/helpers/getPrevRealBlock';
import { useCmsSubscribeToSubsite } from 'modules/cms/hooks/useCmsSubscribeToSubsite';
import { mapCmsBlocksForBody } from 'modules/cms/mappers/mapCmsBlocksForBody';
import { CmsPostPageLayoutBelowBlocks } from 'modules/cms/pages/Post/Layout/CmsPostPageLayoutBelowBlocks';
import { gutterWidth, sectionPadding } from 'theme/theme';
import { trackClicked } from 'utils/analytics/track/trackClicked';
import { trackPageView } from 'utils/analytics/track/trackPageView';

import { Body, Main, Sidebar, SidebarContent } from './CmsPostPageBody.styles';
import { CmsPostPageBodyHeader } from './CmsPostPageBodyHeader';

const Content = styled.div`
  flex: 1 1 auto;
  width: 100%;
`;

type Props = {
  content: CmsApiPagePost;
};

export function CmsPostPageBody({ content }: Props) {
  const { subsite } = content;

  const { subscribe, isSubscribed } = useCmsSubscribeToSubsite(subsite.id);

  useEffect(() => {
    trackPageView('CMS Page', {
      subsite_name: subsite.publicName,
      page_title: content.title,
      page_type: content.type,
      categories: content.categories.map((category) => category.name),
    });
  }, [content, subsite.publicName]);

  const showListingCounts = getContentShowListingCounts({ content });
  const listingCounts = getContentListingCounts({ content });
  const hasHeader = getContentHasBodyHeader({ content });
  const firstRealBlockIndex = getContentFirstRealBlockIndex({ content });

  const mappedBlocks = mapCmsBlocksForBody({ content });

  const firstRealBlock = mappedBlocks[firstRealBlockIndex];
  const firstRealBlockHasBackground = Boolean(
    mappedBlocks?.length && firstRealBlock?.hasBackground,
  );

  const headerBodyHolderWidth =
    content.layout === 'NARROW'
      ? CmsBodyHolderWidth.NARROW_LAYOUT
      : CmsBodyHolderWidth.PAGE_LAYOUT;

  const { layout } = content;

  const hasBelowBlocks = useMemo(
    () =>
      Boolean(
        content.hasRecommendedJobs ||
          (content.tags && content.tags.length > 0) ||
          content.author,
      ),
    [content.author, content.hasRecommendedJobs, content.tags],
  );

  const hasFooter =
    hasBelowBlocks || content.hasSocialSharing || subsite.canSubscribe;

  const main = (
    <Box component="main" data-qa-id="content-container">
      {(hasHeader || content.headerImage || showListingCounts) && (
        <header>
          {hasHeader && (
            <Box m={sectionPadding}>
              <CmsBodyHolder $width={headerBodyHolderWidth}>
                <CmsPostPageBodyHeader content={content} />
              </CmsBodyHolder>
            </Box>
          )}

          {content.headerImage && (
            <Box m={sectionPadding}>
              <CmsBodyHolder $width={headerBodyHolderWidth}>
                <CmsHeaderImage content={content} />
              </CmsBodyHolder>
            </Box>
          )}

          {showListingCounts && listingCounts ? (
            <CmsListingCountsLoadable
              darkMode={firstRealBlockHasBackground}
              listingCounts={listingCounts}
            />
          ) : null}
        </header>
      )}

      <Content data-qa-id="cms-main-content" data-qa-content-layout={layout}>
        {mappedBlocks.map((mappedBlock, index) => {
          if (mappedBlock.type === 'ANCHOR') return null;

          return (
            <CmsBlockContainerWithDividers
              key={mappedBlock.id}
              mappedBlock={mappedBlock}
              prevBlock={getPrevRealBlock(mappedBlocks, index)}
              nextBlock={getNextRealBlock(mappedBlocks, index)}
              layout={content.layout}
              index={index}
              isLastBlock={index === mappedBlocks.length - 1}
            >
              {mappedBlock.component}
            </CmsBlockContainerWithDividers>
          );
        })}

        {hasFooter && <div style={{ marginTop: gutterWidth }} />}
      </Content>

      <CmsPostPageLayoutBelowBlocks content={content} />
    </Box>
  );

  return (
    <Body>
      <Sidebar>
        <SidebarContent>
          {content.tableOfContents.length > 0 && (
            <CmsTableOfContentsLoadable parents={content.tableOfContents} />
          )}
          <CmsSocialSidebar
            listingSearchType={content.listingSearchType}
            hasSocialShare={content.hasSocialSharing}
            hasComments={content.enableComments}
            subscribe={() => {
              subscribe();
              trackClicked('Subscribe', {
                context: 'CMS Page',
              });
            }}
            canSubscribe={subsite.canSubscribe}
            isSubscribed={isSubscribed}
            subsiteSubscribeText={content.subsite.subscribeText}
            shareUrl={getContentLocaleUrl({ content })}
          />
        </SidebarContent>
      </Sidebar>
      <Main>{main}</Main>
    </Body>
  );
}
